import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { Grid, Button } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { toast } from '../../components/toast';
import { useLoadingState } from '../../hooks/useLoadingState';
import DeleteConfirmationDialog from '../../components/common/DeleteConfirmationDialog';
import { IncomeStatsCards, IncomeFilters, IncomeTable, IncomeForm } from '../../components/income';
import { incomeValidationSchema } from '../../utils/incomeValidation';
import {
  getInitialIncomeFormData,
  getIncomeFormDataFromRecord,
  formatIncomeDataForSubmission,
} from '../../utils/incomeUtils';
import {
  getIncomes,
  getIncomeDetails,
  createIncome,
  updateIncome,
  deleteIncome,
  getMoneyAccounts,
} from '../../services/api';
import { getUserGroups } from '../../utils';
import { HRPageHeader } from '../../components/hr';
import { LightTooltip } from '../../components/custom-components/custom-tooltip';

const allowedGroups = ['admin', 'hr'];

export default function IncomeManagementPage() {
  const navigate = useNavigate();
  const [incomes, setIncomes] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedIncome, setSelectedIncome] = useState(null);
  const [dialogMode, setDialogMode] = useState('add');
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [currencyFilter, setCurrencyFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');
  const [dateRange, setDateRange] = useState({
    startDate: null,
    endDate: null,
    preset: 'All',
  });
  const [stats, setStats] = useState({ total_incomes: 0, total_amount: 0, categories_count: 0 });
  const [filterOptions, setFilterOptions] = useState({});
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [incomeToDelete, setIncomeToDelete] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [moneyAccounts, setMoneyAccounts] = useState([]);
  const [formData, setFormData] = useState(getInitialIncomeFormData());
  const { isLoading, withLoading } = useLoadingState({ minLoadingTime: 800 });

  useEffect(() => {
    const userGroups = getUserGroups();
    if (userGroups === null || !allowedGroups.some((group) => userGroups.includes(group))) {
      navigate('/dashboard', { replace: true });
    }
  }, [navigate]);

  const loadMoneyAccounts = useCallback(async () => {
    try {
      const res = await getMoneyAccounts();
      setMoneyAccounts(res.data.results || []);
    } catch (error) {
      console.error('Failed to load money accounts:', error);
      setMoneyAccounts([]);
    }
  }, []);

  const loadIncomes = useCallback(async () => {
    await withLoading(async () => {
      const res = await getIncomes(
        page + 1,
        rowsPerPage,
        searchTerm,
        categoryFilter,
        currencyFilter,
        statusFilter,
        '',
        dateRange.startDate,
        dateRange.endDate
      );
      const data = res.data || {};
      setIncomes(data.results || data.incomes || []);
      setTotalCount(data.count ?? (data.results ? data.results.length : data.incomes?.length || 0));
      if (data.stats) {
        setStats({
          total_incomes: data.stats.total_incomes || 0,
          total_amount: data.stats.total_amount || 0,
          categories_count: Array.isArray(data.stats.category_breakdown) ? data.stats.category_breakdown.length : 0,
        });
      }
      // Update filter options if included in response
      if (data.filter_options) {
        setFilterOptions(data.filter_options);
      }
    });
  }, [
    page,
    rowsPerPage,
    searchTerm,
    categoryFilter,
    currencyFilter,
    statusFilter,
    dateRange.startDate,
    dateRange.endDate,
    withLoading,
  ]);

  useEffect(() => {
    loadIncomes();
  }, [loadIncomes]);

  useEffect(() => {
    loadMoneyAccounts();
  }, [loadMoneyAccounts]);

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenDialog = async (mode, income = null) => {
    setDialogMode(mode);
    setSelectedIncome(income);
    if ((mode === 'edit' || mode === 'view') && income) {
      try {
        const { data } = await getIncomeDetails(income.id);
        const i = data.income || data;
        setFormData(getIncomeFormDataFromRecord(i));
      } catch (_) {
        setFormData(getIncomeFormDataFromRecord(income));
      }
    } else {
      setFormData(getInitialIncomeFormData());
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedIncome(null);
    setFormErrors({});
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = async () => {
    try {
      await incomeValidationSchema.validate(formData, { abortEarly: false });
      setFormErrors({});
      return true;
    } catch (error) {
      const errors = {};
      error.inner.forEach((err) => {
        if (err.path) {
          errors[err.path] = err.message;
        }
      });
      setFormErrors(errors);
      return false;
    }
  };

  const handleSubmit = async () => {
    const isValid = await validateForm();
    if (!isValid) {
      toast.error('Please fill in all required fields correctly');
      return;
    }

    try {
      const payload = formatIncomeDataForSubmission(formData);
      if (dialogMode === 'add') {
        await createIncome(payload);
        toast.success('Income created');
      } else {
        await updateIncome(selectedIncome.id, payload);
        toast.success('Income updated');
      }
      handleCloseDialog();
      loadIncomes();
      loadMoneyAccounts();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to save income');
    }
  };

  const handleDeleteIncome = (income) => {
    setIncomeToDelete(income);
    setOpenDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (incomeToDelete) {
      try {
        await deleteIncome(incomeToDelete.id);
        toast.success('Income deleted successfully');
        setOpenDeleteDialog(false);
        setIncomeToDelete(null);
        loadIncomes();
        loadMoneyAccounts();
      } catch (err) {
        toast.error(err?.response?.data?.message || 'Failed to delete income');
      }
    }
  };

  const handleCancelDelete = () => {
    setOpenDeleteDialog(false);
    setIncomeToDelete(null);
  };

  const handleDateRangeChange = (newDateRange) => {
    setDateRange(newDateRange);
    setPage(0);
  };

  return (
    <>
      <Helmet>
        <title>BillerBay Transmit | Income Management</title>
      </Helmet>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <HRPageHeader
            title="Income management"
            description="Record income transactions and tie them to money accounts. Use filters to reconcile entries by date, category, or account."
            actions={
              <LightTooltip title="Record a new income entry.">
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog('add')}>
                  Add income
                </Button>
              </LightTooltip>
            }
          />
        </Grid>
        <Grid item xs={12}>
          <IncomeStatsCards stats={stats} isLoading={isLoading} />
        </Grid>

        <IncomeFilters
          searchTerm={searchTerm}
          categoryFilter={categoryFilter}
          currencyFilter={currencyFilter}
          statusFilter={statusFilter}
          dateRange={dateRange}
          filterOptions={filterOptions}
          onSearchChange={(value) => {
            setSearchTerm(value);
            setPage(0);
          }}
          onCategoryFilterChange={(value) => {
            setCategoryFilter(value);
            setPage(0);
          }}
          onCurrencyFilterChange={(value) => {
            setCurrencyFilter(value);
            setPage(0);
          }}
          onStatusFilterChange={(value) => {
            setStatusFilter(value);
            setPage(0);
          }}
          onDateRangeChange={handleDateRangeChange}
          onClearFilters={() => {
            setSearchTerm('');
            setCategoryFilter('All');
            setCurrencyFilter('All');
            setStatusFilter('All');
            setDateRange({
              startDate: null,
              endDate: null,
              preset: 'All',
            });
            setPage(0);
          }}
        />

        <IncomeTable
          incomes={incomes}
          isLoading={isLoading}
          page={page}
          rowsPerPage={rowsPerPage}
          totalCount={totalCount}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          onView={(income) => handleOpenDialog('view', income)}
          onEdit={(income) => handleOpenDialog('edit', income)}
          onDelete={handleDeleteIncome}
        />
      </Grid>

      <IncomeForm
        open={openDialog}
        mode={dialogMode}
        formData={formData}
        formErrors={formErrors}
        moneyAccounts={moneyAccounts}
        onClose={handleCloseDialog}
        onSubmit={handleSubmit}
        onInputChange={handleInputChange}
      />

      <DeleteConfirmationDialog
        open={openDeleteDialog}
        title="Confirm Delete"
        message="Are you sure you want to delete the income"
        itemName={incomeToDelete?.name}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        confirmButtonText="Delete Income"
      />
    </>
  );
}
